import React, { memo } from "react";
import { MdEmail, MdOutlineSchool, MdEdit, MdDelete } from "react-icons/md";
import { motion } from "framer-motion";
import { usePhotoUrl } from "../../../../utils/usePhotoUrl";

const AuthorCard = ({ author, isGridView, onEdit, onDelete }) => {
  const { getPhotoUrl } = usePhotoUrl();
  const countAffiliations = (affiliations) => {
    if (Array.isArray(affiliations)) {
      return affiliations.length;
    }
    if (typeof affiliations === "string" && affiliations.includes(",")) {
      return affiliations.split(",").length;
    }
    return affiliations ? 1 : 0;
  };

  const affiliationCount = countAffiliations(author.id_aff);

  // Handle edit button click
  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit) onEdit(author);
  };

  // Handle delete button click
  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) onDelete(author);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`rounded-xl bg-white p-5 shadow-sm transition-all hover:shadow-md ${
        isGridView ? "" : "flex items-center"
      }`}
    >
      {isGridView ? (
        <div className="text-center relative">
          {/* Action buttons in grid view */}
          <div className="absolute right-0 top-0 flex gap-2">
            <button
              onClick={handleEdit}
              className="rounded-full bg-blue-50 p-2 text-blue-600 hover:bg-blue-100 transition-colors"
              aria-label="Modifier"
            >
              <MdEdit size={16} />
            </button>
            <button
              onClick={handleDelete}
              className="rounded-full bg-red-50 p-2 text-red-600 hover:bg-red-100 transition-colors"
              aria-label="Supprimer"
            >
              <MdDelete size={16} />
            </button>
          </div>

          <div className="mx-auto h-20 w-20 overflow-hidden rounded-full border-2 border-gray-200">
            <img
              src={getPhotoUrl(author.image_aut)}
              alt={`${author.first_name} ${author.last_name}`}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="mt-3">
            <h3 className="font-semibold text-gray-800">
              {author.title_aut && `${author.title_aut} `}
              {author.first_name} {author.last_name}
            </h3>
            <div className="mt-2 flex items-center justify-center">
              <MdEmail className="mr-1 text-[#57A863]" />
              <p className="text-sm text-gray-600">{author.email_user}</p>
            </div>

            {author.id_aff && (
              <div className="mt-1 flex items-center justify-center">
                <MdOutlineSchool className="mr-1 text-[#57A863]" />
                <p className="text-sm text-gray-600">
                  {affiliationCount} affiliation(s)
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex w-full items-center">
          <div className="mr-4 h-16 w-16 overflow-hidden rounded-full border-2 border-gray-200">
            <img
              src={getPhotoUrl("storage/" + author.image_aut)}
              alt={`${author.first_name} ${author.last_name}`}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="flex-grow">
            <h3 className="text-lg font-semibold text-gray-800">
              {author.title_aut && `${author.title_aut} `}
              {author.first_name} {author.last_name}
            </h3>
            <div className="mt-1 flex flex-col space-y-1">
              <div className="flex items-center text-gray-600">
                <MdEmail className="mr-1 text-[#57A863]" />
                <span className="text-sm">{author.email_user}</span>
              </div>

              {author.id_aff && (
                <div className="flex items-center text-gray-600">
                  <MdOutlineSchool className="mr-1 text-[#57A863]" />
                  <span className="text-sm">
                    {affiliationCount} affiliation(s)
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Action buttons in list view */}
          <div className="ml-4 flex items-center gap-2">
            <button
              onClick={handleEdit}
              className="rounded-full bg-blue-50 p-2 text-blue-600 hover:bg-blue-100 transition-colors"
              aria-label="Modifier"
            >
              <MdEdit size={18} />
            </button>
            <button
              onClick={handleDelete}
              className="rounded-full bg-red-50 p-2 text-red-600 hover:bg-red-100 transition-colors"
              aria-label="Supprimer"
            >
              <MdDelete size={18} />
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default memo(AuthorCard);